import { Box, Stack } from '@mui/material';
import { Typography, IconButton, colors } from '@sweep-io/sweep-design';
import { Close, Layout, RelatedObjects, CloudArrowDown } from '@sweep-io/sweep-design/dist/icons';
import { useState, useCallback, useEffect } from 'react';
import { ZeroStateActionCard } from './ZeroStateActionCard';
import { ProcessMiningPanel } from '../process-mining/ProcessMiningPanel';
import { usePanels } from '../../../../panels/usePanels';
import { templatesPanelType } from '../../../funnel-map-flow/templates/dialog/panelTypes';
import { metadataElementsPanelType } from '../../../funnel-map-flow/dialogs/metadata-elements/metadataElementsPanelType';
import { importFunnelPanelType } from '../../../funnel-map-flow/dialogs/import-funnel/importFunnelPanelType';
import { useCanvasToolbarContext } from '../canvas-toolbar/CanvasToolbarCtx';
import ProcessMiningColored from '../process-mining/svg/process-mining-colored.svg?react';
import { useProcessMining } from '../useProcessMining';
import { usePageHelpers } from '../usePageHelpers';

type ZeroStateView = 'initial' | 'processMiningPanel' | 'importOptions';

export const ZeroStatePanel = () => {
  const [view, setView] = useState<ZeroStateView>('initial');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState<'left' | 'right'>('right');
  const { maybeOpenPanel, maybeCloseActivePanel, isPanelOpen } = usePanels();
  const { setActiveExclusiveButton } = useCanvasToolbarContext();
  const { funnelMapId } = usePageHelpers();
  const { isLoading: isLoadingProcessMining } = useProcessMining(
    funnelMapId || '',
  );

  const handleViewChange = useCallback((newView: ZeroStateView) => {
    // Determine transition direction based on current and target view
    const isExitingInitial = view === 'initial' && newView !== 'initial';
    const isEnteringInitial = view !== 'initial' && newView === 'initial';

    if (isExitingInitial) {
      setTransitionDirection('right'); // Exit initial: slide right-to-left
    } else if (isEnteringInitial) {
      setTransitionDirection('left'); // Enter initial: slide left-to-right
    }

    setIsTransitioning(true);
    setTimeout(() => {
      setView(newView);
      setIsTransitioning(false);
    }, 150); // Half of the transition duration for smooth crossfade
  }, [view]);

  const handleBackToInitial = useCallback(() => {
    maybeCloseActivePanel();
    handleViewChange('initial');
  }, [maybeCloseActivePanel, handleViewChange]);

  const handleTemplatesClick = useCallback(() => {
    maybeOpenPanel({
      panelType: templatesPanelType,
      onOpen: () => setActiveExclusiveButton('templatesAndPlugins'),
      onClose: () => setActiveExclusiveButton(null),
    });
  }, [maybeOpenPanel, setActiveExclusiveButton]);

  const handleMetadataClick = useCallback(() => {
    maybeOpenPanel({
      panelType: metadataElementsPanelType,
      onOpen: () => setActiveExclusiveButton('metadataElements'),
      onClose: () => setActiveExclusiveButton(null),
    });
  }, [maybeOpenPanel, setActiveExclusiveButton]);

  const handleImportFunnelClick = useCallback(() => {
    maybeOpenPanel({ panelType: importFunnelPanelType });
  }, [maybeOpenPanel]);

  const handleMapWithAIClick = useCallback(() => {
    maybeCloseActivePanel();
    handleViewChange('processMiningPanel');
  }, [maybeCloseActivePanel, handleViewChange]);

  const handleAddFromSalesforceClick = useCallback(() => {
    maybeCloseActivePanel();
    handleViewChange('importOptions');
  }, [maybeCloseActivePanel, handleViewChange]);

  const renderContent = () => {
    switch (view) {
      case 'initial':
        return (
          <Stack alignItems="center" gap={4}>
            <Typography variant="h1" color={colors.grey[800]}>
              How do you want to get started?
            </Typography>

            <Stack direction="row" gap={2} justifyContent="space-between" sx={{ width: '680px' }}>
              <ZeroStateActionCard
                icon={<Stack width="24px"><ProcessMiningColored /></Stack>}
                name="Map with AI"
                description="Describe the metadata you want AI to map"
                onClick={handleMapWithAIClick}
              />

              <ZeroStateActionCard
                icon={<Layout size="large" />}
                name="Explore Templates"
                description="Start with ready-made process templates"
                onClick={handleTemplatesClick}
                isActive={isPanelOpen(templatesPanelType)}
              />

              <ZeroStateActionCard
                icon={<RelatedObjects size="large" />}
                name="Add from Salesforce"
                description="Import metadata directly from your org"
                onClick={handleAddFromSalesforceClick}
              />
            </Stack>
          </Stack>
        );

      case 'processMiningPanel':
        return <ProcessMiningPanel onClose={handleBackToInitial} />;

      case 'importOptions':
        return (
          <Stack alignItems="center" gap={4}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h1" color={colors.grey[800]}>
                Add from Salesforce
              </Typography>
              <Box
                sx={{
                  position: 'absolute',
                  top: '4px',
                  right: '0',
                }}
              >
                <IconButton variant="system" size="tiny" onClick={handleBackToInitial}>
                  <Close />
                </IconButton>
              </Box>
            </Box>
            <Stack gap={2} sx={{ width: '400px' }}>
              <ZeroStateActionCard
                icon={<RelatedObjects size="large" />}
                name="Metadata Elements"
                description="Import metadata from your Salesforce org"
                onClick={handleMetadataClick}
                isActive={isPanelOpen(metadataElementsPanelType)}
              />

              <ZeroStateActionCard
                icon={<CloudArrowDown size="large" />}
                name="Business Processes"
                description="Import business processes from your environment"
                onClick={handleImportFunnelClick}
                isActive={isPanelOpen(importFunnelPanelType)}
              />
            </Stack>
          </Stack>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    if (isLoadingProcessMining) {
      setView('processMiningPanel')
    }
  }, [isLoadingProcessMining]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
        transition: 'all 0.2s ease',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'transparent',
          boxSizing: 'border-box',
          padding: 0,
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning
            ? `translateX(${transitionDirection === 'right' ? '20px' : '-20px'})`
            : 'translateX(0)',
          transition: 'opacity 0.3s ease, transform 0.3s ease',
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};
